// ----------------------------------------------------------------------------
// step-sequencer — Step sequencer panel for DAWSEUR
//
// Layout: 2 columns
//   Left: list of sequences (create, select, delete, rename)
//   Right: step editor (channels x steps grid, per-channel sample + volume)
//
// Sequences are embedded per-clip when dragged to the timeline.
// Samples come from the DAWSEUR bucket.
// ----------------------------------------------------------------------------

const { makeKnob } = require('./knob.js');

// ---- Draw a mini waveform on a canvas ----
function drawChannelWaveform(canvas, buffer) {
   const ctx = canvas.getContext('2d');
   const w = canvas.width;
   const h = canvas.height;
   ctx.clearRect(0, 0, w, h);

   const data = buffer.getChannelData(0);
   const samplesPerPixel = Math.floor(data.length / w);
   if (samplesPerPixel < 1) return;

   ctx.strokeStyle = 'var(--c9)';
   ctx.fillStyle = 'var(--c9)';
   ctx.lineWidth = 1;
   const mid = h / 2;

   for (let x = 0; x < w; x++) {
      let min = 1, max = -1;
      const start = x * samplesPerPixel;
      const end = Math.min(start + samplesPerPixel, data.length);
      for (let i = start; i < end; i++) {
         const v = data[i];
         if (v < min) min = v;
         if (v > max) max = v;
      }
      const yMin = mid + min * mid;
      const yMax = mid + max * mid;
      ctx.beginPath();
      ctx.moveTo(x, yMin);
      ctx.lineTo(x, yMax);
      ctx.stroke();
   }
}

function createStepSequencer(opts = {}) {
   const container = document.getElementById('lw-stepseq-body');
   if (!container) return null;

   const state = {
      sequences: [],        // array of sequence objects
      activeSeqIdx: 0,      // currently selected sequence
      onSequenceDrag: opts.onSequenceDrag || (() => {}), // drag to timeline
      getBucketItems: opts.getBucketItems || (() => []),
      getEngine: opts.getEngine || (() => null),
      onChange: opts.onChange || (() => {}), // called when sequence data changes
      resolveDropItem: opts.resolveDropItem || null,
      // Preview player state
      previewPlaying: false,
      previewSchedulerId: null,
      previewNextStepTime: 0,
      previewCurrentStep: 0,
      previewStepCells: [],  // refs to step cells for highlighting
   };

   // ---- Sequence object structure ----
   // {
   //   name: string,
   //   steps: number,           // e.g. 16
   //   bpm: number,             // sequence tempo
   //   channels: [
   //     {
   //       sampleId: string,    // bucket item id
   //       sampleName: string,  // display name
   //       volume: number,      // 0..1
   //       steps: boolean[],    // on/off per step
   //     }
   //   ]
   // }

   // ---- Build UI ----
   container.innerHTML = '';

   const wrapper = document.createElement('div');
   wrapper.className = 'lw-stepseq-wrapper';
   wrapper.style.cssText = 'display:flex;flex-direction:row;width:100%;height:100%;overflow:hidden;';

   // Left column: sequence list
   const leftCol = document.createElement('div');
   leftCol.className = 'lw-stepseq-list-col';
   leftCol.style.cssText = 'width:180px;min-width:120px;border-right:1px solid var(--c2);display:flex;flex-direction:column;overflow:hidden;';

   const listHeader = document.createElement('div');
   listHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:4px;';
   listHeader.innerHTML = '<span style="font-family:bit,monospace;font-size:10px;color:var(--ce);flex:1;">Sequences</span>';
   const addBtn = document.createElement('button');
   addBtn.textContent = '+';
   addBtn.className = 'lw-btn-sm';
   addBtn.title = 'New sequence';
   addBtn.onclick = () => createNewSequence();
   listHeader.appendChild(addBtn);
   leftCol.appendChild(listHeader);

   const listEl = document.createElement('div');
   listEl.className = 'lw-stepseq-list';
   listEl.style.cssText = 'flex:1;overflow-y:auto;';
   leftCol.appendChild(listEl);

   // Right column: editor
   const rightCol = document.createElement('div');
   rightCol.className = 'lw-stepseq-editor-col';
   rightCol.style.cssText = 'flex:1;display:flex;flex-direction:column;overflow:hidden;';

   const editorHeader = document.createElement('div');
   editorHeader.className = 'lw-stepseq-editor-header';
   editorHeader.style.cssText = 'padding:4px 8px;border-bottom:1px solid var(--c2);display:flex;align-items:center;gap:6px;flex-wrap:wrap;';
   rightCol.appendChild(editorHeader);

   const editorBody = document.createElement('div');
   editorBody.className = 'lw-stepseq-editor-body';
   editorBody.style.cssText = 'flex:1;overflow-y:auto;';
   rightCol.appendChild(editorBody);

   // ---- Drag & drop bucket samples onto editor to add channels ----
   rightCol.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      rightCol.style.outline = '1px dashed var(--c9)';
   });
   rightCol.addEventListener('dragleave', () => {
      rightCol.style.outline = '';
   });
   rightCol.addEventListener('drop', async (e) => {
      e.preventDefault();
      rightCol.style.outline = '';
      // Try to resolve as bucket item or samplebank drag
      if (state.resolveDropItem) {
         const item = await state.resolveDropItem(e.dataTransfer);
         if (item) {
            addChannelFromSample(item.id, item.name);
            return;
         }
      }
      // Fallback: bucket item ID lookup
      const itemId = e.dataTransfer.getData('application/x-bucket-item-id') ||
                     e.dataTransfer.getData('text/plain');
      if (itemId && state.getBucketItems) {
         const item = state.getBucketItems().find(it => it.id === itemId);
         if (item) {
            addChannelFromSample(item.id, item.name);
            return;
         }
      }
      // Also accept file drops
      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
         for (const file of files) {
            if (file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|flac|ogg|aif+)$/i)) {
               addChannelFromSample(file.name, file.name);
            }
         }
      }
   });

   wrapper.appendChild(leftCol);
   wrapper.appendChild(rightCol);
   container.appendChild(wrapper);

   // ---- Create a new sequence ----
   function createNewSequence() {
      const seq = {
         uid: 'seq_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
         name: 'Sequence ' + (state.sequences.length + 1),
         steps: 16,
         bpm: parseFloat(document.getElementById('lw-bpm').value) || 120,
         channels: [],  // empty by default — add channels by dropping samples from bucket
      };
      state.sequences.push(seq);
      state.activeSeqIdx = state.sequences.length - 1;
      renderList();
      renderEditor();
   }

   // ---- Add a channel from a bucket sample (drag & drop) ----
   function addChannelFromSample(sampleId, sampleName) {
      const seq = state.sequences[state.activeSeqIdx];
      if (!seq) return;
      seq.channels.push({
         id: 'ch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
         sampleId: sampleId,
         sampleName: sampleName || sampleId,
         volume: 0.8,
         steps: new Array(seq.steps).fill(false),
      });
      renderEditor();
      state.onChange();
   }

   // ---- Render sequence list ----
   function renderList() {
      listEl.innerHTML = '';
      for (let i = 0; i < state.sequences.length; i++) {
         const seq = state.sequences[i];
         const item = document.createElement('div');
         item.className = 'lw-stepseq-list-item';
         item.style.cssText = 'padding:4px 8px;cursor:pointer;font-family:bit,monospace;font-size:10px;display:flex;align-items:center;gap:4px;border-bottom:1px solid var(--c2);';
         if (i === state.activeSeqIdx) {
            item.style.background = 'var(--c3)';
            item.style.color = 'var(--cf)';
         } else {
            item.style.color = 'var(--ce)';
         }
         item.textContent = seq.name;
         item.draggable = true;
         item.onclick = () => {
            if (state.previewPlaying) stopPreview();
            state.activeSeqIdx = i;
            renderList();
            renderEditor();
         };
         item.ondragstart = (e) => {
            e.dataTransfer.setData('text/dawseur-stepseq', JSON.stringify({
               type: 'stepseq',
               sequenceIdx: i,
            }));
            e.dataTransfer.effectAllowed = 'copy';
         };
         // Rename on double-click
         item.ondblclick = () => {
            const name = prompt('Sequence name:', seq.name);
            if (name) { seq.name = name; renderList(); }
         };
         // Delete button
         const delBtn = document.createElement('span');
         delBtn.textContent = 'x';
         delBtn.style.cssText = 'margin-left:auto;color:var(--c6);cursor:pointer;';
         delBtn.onclick = (e) => {
            e.stopPropagation();
            state.sequences.splice(i, 1);
            if (state.activeSeqIdx >= state.sequences.length) state.activeSeqIdx = state.sequences.length - 1;
            renderList();
            renderEditor();
         };
         item.appendChild(delBtn);
         listEl.appendChild(item);
      }
      if (state.sequences.length === 0) {
         listEl.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">No sequences. Click + to create one.</div>';
      }
   }

   // ---- Preview player: loop the active sequence independently ----
   // ---- Preview player: loop the active sequence independently ----
   function startPreview() {
      const seq = state.sequences[state.activeSeqIdx];
      if (!seq || seq.channels.length === 0) return;
      const eng = state.getEngine();
      if (!eng) return;

      stopPreview();

      // Stop the main transport if playing — preview is solo
      if (eng.state.isPlaying) {
         eng.stop();
      }

      state.previewPlaying = true;
      state.previewCurrentStep = 0;
      const ctx = eng.ctx;
      // Resume AudioContext if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
         ctx.resume();
      }

      const secPerBeat = 60 / (seq.bpm || parseFloat(document.getElementById('lw-bpm').value) || 120);
      const secPerStep = secPerBeat / 4;
      state.previewNextStepTime = ctx.currentTime + 0.2;

      // Connect preview directly to destination (bypass master chain)
      const previewGain = ctx.createGain();
      previewGain.gain.value = 0.8;
      previewGain.connect(ctx.destination);
      state.previewGain = previewGain;

      // Kick off async loading of all samples (non-blocking)
      const sampleIds = [...new Set(seq.channels.map(ch => ch.sampleId).filter(Boolean))];
      for (const id of sampleIds) {
         eng.getMIDISampleBuffer(id); // triggers async load + cache
      }

      function scheduler() {
         if (!state.previewPlaying) return;
         try {
            const lookAhead = 0.2;
            while (state.previewNextStepTime < ctx.currentTime + lookAhead) {
               const step = state.previewCurrentStep;
               const whenSec = state.previewNextStepTime;

               // Trigger active channels for this step
               for (const ch of seq.channels) {
                  if (!ch.sampleId || !ch.steps) continue;
                  const stepVal = ch.steps[step];
                  if (stepVal === false || stepVal === undefined || stepVal === null) continue;
                  const buffer = eng.getMIDISampleBuffer(ch.sampleId);
                  if (buffer) {
                     const semitone = typeof stepVal === 'number' ? stepVal : 0;
                     const playbackRate = Math.pow(2, semitone / 12);
                     const vel = (ch.velocities && ch.velocities[step] !== undefined) ? ch.velocities[step] : 1.0;
                     const amp = ch.volume * vel;
                     if (eng.triggerSampleMono) {
                        eng.triggerSampleMono('stepseq:' + ch.id, buffer, previewGain, whenSec, amp, playbackRate, 0);
                     } else {
                        eng.triggerSample(buffer, previewGain, whenSec, amp, playbackRate, 0);
                     }
                  }
               }

               // Schedule UI highlight
               const highlightStep = step;
               const highlightTime = whenSec - ctx.currentTime;
               setTimeout(() => {
                  if (state.previewPlaying) highlightStepUI(highlightStep);
               }, Math.max(0, highlightTime * 1000));

               state.previewCurrentStep = (state.previewCurrentStep + 1) % seq.steps;
               state.previewNextStepTime += secPerStep;
            }
         } catch (e) {
            console.error('[stepseq] scheduler error:', e);
         }
         state.previewSchedulerId = setTimeout(scheduler, 25);
      }
      scheduler();

      // Update play button
      updatePlayButton();
   }
   function stopPreview() {
      state.previewPlaying = false;
      if (state.previewSchedulerId) {
         clearTimeout(state.previewSchedulerId);
         state.previewSchedulerId = null;
      }
      clearStepHighlight();
      updatePlayButton();
   }

   function togglePreview() {
      if (state.previewPlaying) stopPreview();
      else startPreview();
   }

   function updatePlayButton() {
      const btn = document.getElementById('lw-stepseq-preview-btn');
      if (btn) {
         btn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
         btn.style.color = state.previewPlaying ? 'var(--c6)' : 'var(--c9)';
      }
   }

   function highlightStepUI(step) {
      clearStepHighlight();
      for (const cell of state.previewStepCells) {
         if (cell && cell.dataset.step === String(step)) {
            cell.style.outline = '2px solid var(--c9)';
            cell.style.outlineOffset = '-1px';
         }
      }
   }

   function clearStepHighlight() {
      for (const cell of state.previewStepCells) {
         if (cell) {
            cell.style.outline = '';
            cell.style.outlineOffset = '';
         }
      }
   }

   // ---- Render step editor ----
   // ---- QWERTY pitch keyboard popup ----
   // Shows a virtual keyboard to set per-step pitch (semitone offset)
   function showPitchKeyboard(e, ch, stepIdx, onChange) {
      // Close any existing keyboard
      const existing = document.querySelector('.lw-step-pitch-kb');
      if (existing) existing.remove();

      const currentVal = typeof ch.steps[stepIdx] === 'number' ? ch.steps[stepIdx] : 0;
      let octave = 0; // octave offset for the keyboard

      const popup = document.createElement('div');
      popup.className = 'lw-step-pitch-kb';
      popup.style.cssText = 'position:fixed;z-index:99999;background:var(--c1);border:1px solid var(--c4);border-radius:3px;box-shadow:0 4px 12px rgba(0,0,0,0.5);font-family:bit,monospace;font-size:10px;padding:6px;display:flex;flex-direction:column;gap:4px;';

      // Header
      const header = document.createElement('div');
      header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;color:var(--ce);';
      const title = document.createElement('span');
      title.textContent = 'Step ' + (stepIdx + 1) + ' pitch';
      header.appendChild(title);

      const closeBtn = document.createElement('span');
      closeBtn.textContent = 'x';
      closeBtn.style.cssText = 'cursor:pointer;color:var(--c6);';
      closeBtn.onclick = () => popup.remove();
      header.appendChild(closeBtn);
      popup.appendChild(header);

      // Current value display
      const valDisplay = document.createElement('div');
      valDisplay.style.cssText = 'text-align:center;color:var(--c9);font-size:11px;padding:2px;';
      function updateValDisplay() {
         const v = typeof ch.steps[stepIdx] === 'number' ? ch.steps[stepIdx] : 0;
         valDisplay.textContent = (v > 0 ? '+' : '') + v + ' semitones' + (v === 0 ? ' (original)' : '');
      }
      updateValDisplay();
      popup.appendChild(valDisplay);

      // Octave controls
      const octaveRow = document.createElement('div');
      octaveRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;';
      const octDown = document.createElement('button');
      octDown.textContent = 'Oct-';
      octDown.className = 'lw-btn-text';
      octDown.style.cssText = 'padding:2px 6px;font-size:9px;';
      const octLabel = document.createElement('span');
      octLabel.style.cssText = 'color:var(--ce);min-width:30px;text-align:center;';
      octLabel.textContent = 'Oct 0';
      const octUp = document.createElement('button');
      octUp.textContent = 'Oct+';
      octUp.className = 'lw-btn-text';
      octUp.style.cssText = 'padding:2px 6px;font-size:9px;';
      octDown.onclick = () => { octave--; octLabel.textContent = 'Oct ' + octave; drawKeyboard(); };
      octUp.onclick = () => { octave++; octLabel.textContent = 'Oct ' + octave; drawKeyboard(); };
      octaveRow.appendChild(octDown);
      octaveRow.appendChild(octLabel);
      octaveRow.appendChild(octUp);
      popup.appendChild(octaveRow);

      // Keyboard area
      const kbWrap = document.createElement('div');
      kbWrap.style.cssText = 'position:relative;display:flex;gap:1px;';
      popup.appendChild(kbWrap);

      // QWERTY key -> semitone mapping (Hangcock layout: bottom row = white keys, middle = sharps)
      // Using a piano layout mapped to QWERTY:
      // Z=S D F G H J K = C D E F G A B C (white keys, bottom row)
      // S D _ G H J = C# D# F# G# A# (black keys, above)
      // This is the standard "tracker" / Schism keyboard layout
      const WHITE_KEYS = [
         { key: 'Z', note: 0 },   // C
         { key: 'X', note: 2 },   // D
         { key: 'C', note: 4 },   // E
         { key: 'V', note: 5 },   // F
         { key: 'B', note: 7 },   // G
         { key: 'N', note: 9 },   // A
         { key: 'M', note: 11 },  // B
         { key: ',', note: 12 },  // C (next octave)
      ];
      const BLACK_KEYS = [
         { key: 'S', note: 1, afterWhite: 0 },  // C# (after Z)
         { key: 'D', note: 3, afterWhite: 1 },  // D# (after X)
         { key: 'G', note: 6, afterWhite: 3 },  // F# (after V)
         { key: 'H', note: 8, afterWhite: 4 },  // G# (after B)
         { key: 'J', note: 10, afterWhite: 5 }, // A# (after N)
      ];

      function drawKeyboard() {
         kbWrap.innerHTML = '';
         const whiteKeyW = 22, whiteKeyH = 50, blackKeyW = 14, blackKeyH = 32;

         // White keys row
         const whiteRow = document.createElement('div');
         whiteRow.style.cssText = 'display:flex;gap:1px;position:relative;';

         for (const wk of WHITE_KEYS) {
            const semitone = wk.note + octave * 12;
            const keyEl = document.createElement('div');
            const isActive = currentVal === semitone;
            keyEl.style.cssText = `width:${whiteKeyW}px;height:${whiteKeyH}px;background:${isActive ? 'var(--c9)' : 'var(--ce)'};border:1px solid var(--c3);display:flex;flex-direction:column;align-items:center;justify-content:flex-end;cursor:pointer;color:var(--c0);font-size:8px;padding:1px;`;
            keyEl.innerHTML = `<span style="font-size:7px;opacity:0.7;">${wk.key}</span><span>${semitone >= 0 ? '+' : ''}${semitone}</span>`;
            keyEl.onclick = () => {
               ch.steps[stepIdx] = semitone;
               updateValDisplay();
               drawKeyboard();
               onChange();
            };
            keyEl.oncontextmenu = (ev) => {
               ev.preventDefault();
               // Right-click on key = preview the pitch
               previewPitch(ch, semitone);
            };
            whiteRow.appendChild(keyEl);
         }

         // Black keys overlaid
         for (const bk of BLACK_KEYS) {
            const semitone = bk.note + octave * 12;
            const isActive = currentVal === semitone;
            const keyEl = document.createElement('div');
            keyEl.style.cssText = `position:absolute;width:${blackKeyW}px;height:${blackKeyH}px;background:${isActive ? 'var(--c9)' : 'var(--c0)'};border:1px solid var(--c3);left:${bk.afterWhite * (whiteKeyW + 1) + (whiteKeyW - blackKeyW / 2) - 1}px;top:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;cursor:pointer;color:var(--ce);font-size:7px;padding:1px;`;
            keyEl.innerHTML = `<span style="opacity:0.7;">${bk.key}</span><span>${semitone >= 0 ? '+' : ''}${semitone}</span>`;
            keyEl.onclick = () => {
               ch.steps[stepIdx] = semitone;
               updateValDisplay();
               drawKeyboard();
               onChange();
            };
            keyEl.oncontextmenu = (ev) => {
               ev.preventDefault();
               previewPitch(ch, semitone);
            };
            whiteRow.appendChild(keyEl);
         }

         kbWrap.appendChild(whiteRow);
      }

      function previewPitch(ch, semitone) {
         const eng = state.getEngine();
         if (!eng || !ch.sampleId) return;
         const buffer = eng.getMIDISampleBuffer(ch.sampleId);
         if (!buffer) return;
         const ctx = eng.ctx;
         if (ctx.state === 'suspended') ctx.resume();
         const playbackRate = Math.pow(2, semitone / 12);
         const tmpGain = ctx.createGain();
         tmpGain.gain.value = 0.5;
         tmpGain.connect(ctx.destination);
         if (eng.triggerSampleMono) {
            eng.triggerSampleMono('stepseq_preview', buffer, tmpGain, ctx.currentTime + 0.01, 0.5, playbackRate, 0);
         } else {
            eng.triggerSample(buffer, tmpGain, ctx.currentTime + 0.01, 0.5, playbackRate, 0);
         }
         setTimeout(() => { try { tmpGain.disconnect(); } catch (e) {} }, 2000);
      }

      drawKeyboard();

      // Reset button
      const resetRow = document.createElement('div');
      resetRow.style.cssText = 'display:flex;gap:4px;justify-content:center;';
      const resetBtn = document.createElement('button');
      resetBtn.textContent = 'Reset (0)';
      resetBtn.className = 'lw-btn-text';
      resetBtn.style.cssText = 'padding:2px 8px;font-size:9px;';
      resetBtn.onclick = () => {
         ch.steps[stepIdx] = 0;
         updateValDisplay();
         drawKeyboard();
         onChange();
      };
      resetRow.appendChild(resetBtn);

      const offBtn = document.createElement('button');
      offBtn.textContent = 'Step Off';
      offBtn.className = 'lw-btn-text';
      offBtn.style.cssText = 'padding:2px 8px;font-size:9px;';
      offBtn.onclick = () => {
         ch.steps[stepIdx] = false;
         popup.remove();
         onChange();
      };
      resetRow.appendChild(offBtn);
      popup.appendChild(resetRow);

      // Position at cursor
      document.body.appendChild(popup);
      let px = e.clientX, py = e.clientY;
      const rect = popup.getBoundingClientRect();
      if (px + rect.width > window.innerWidth) px = window.innerWidth - rect.width - 4;
      if (py + rect.height > window.innerHeight) py = window.innerHeight - rect.height - 4;
      popup.style.left = px + 'px';
      popup.style.top = py + 'px';

      // Close on outside click
      setTimeout(() => {
         const closeHandler = (ev) => {
            if (!popup.contains(ev.target)) {
               popup.remove();
               document.removeEventListener('mousedown', closeHandler, true);
            }
         };
         document.addEventListener('mousedown', closeHandler, true);
      }, 0);
   }

   function renderEditor() {
      const seq = state.sequences[state.activeSeqIdx];
      // Destroy old knobs before clearing (cleans up registry entries)
      const oldKnobs = editorBody.querySelectorAll('.lw-mx-knob');
      for (const k of oldKnobs) {
         if (k.destroy) k.destroy();
      }
      editorHeader.innerHTML = '';
      editorBody.innerHTML = '';
      if (!seq) {
         editorBody.innerHTML = '<div style="padding:8px;font-family:bit,monospace;font-size:10px;color:var(--c6);">Select or create a sequence.</div>';
         return;
      }

      // Preview play/pause button
      const playBtn = document.createElement('button');
      playBtn.id = 'lw-stepseq-preview-btn';
      playBtn.className = 'lw-btn-sm';
      playBtn.textContent = state.previewPlaying ? '\uE801' : '\uE800';
      playBtn.title = 'Preview sequence (loop)';
      playBtn.style.cssText = 'font-size:12px;color:var(--c9);min-width:24px;font-family:icons;';
      playBtn.onclick = () => togglePreview();
      editorHeader.appendChild(playBtn);

      // Header controls
      const stepsLabel = document.createElement('span');
      stepsLabel.style.cssText = 'font-family:bit,monospace;font-size:10px;color:var(--ce);margin-left:4px;';
      stepsLabel.textContent = 'Steps:';
      editorHeader.appendChild(stepsLabel);

      const stepsSelect = document.createElement('select');
      stepsSelect.className = 'lw-select';
      stepsSelect.style.cssText = 'background:var(--c0);border:1px solid var(--c4);color:var(--cf);font-family:bit,monospace;font-size:10px;';
      [8, 16, 24, 32, 64].forEach(n => {
         const opt = document.createElement('option');
         opt.value = n;
         opt.textContent = n;
         if (n === seq.steps) opt.selected = true;
         stepsSelect.appendChild(opt);
      });
      stepsSelect.onchange = () => {
         const newSteps = parseInt(stepsSelect.value);
         for (const ch of seq.channels) {
            if (ch.steps.length < newSteps) {
               ch.steps = ch.steps.concat(new Array(newSteps - ch.steps.length).fill(false));
            } else {
               ch.steps = ch.steps.slice(0, newSteps);
            }
         }
         seq.steps = newSteps;
         renderEditor();
         state.onChange();
      };
      editorHeader.appendChild(stepsSelect);

      // Velocity mode toggle
      const velModeBtn = document.createElement('button');
      velModeBtn.textContent = 'VEL';
      velModeBtn.className = 'lw-btn-text';
      velModeBtn.style.cssText = 'margin-left:4px;font-size:9px;padding:1px 6px;' + (state.velMode ? 'background:var(--cb);color:var(--c0);' : '');
      velModeBtn.title = 'Toggle velocity edit mode (drag up/down on steps to set velocity)';
      velModeBtn.onclick = () => {
         state.velMode = !state.velMode;
         renderEditor();
      };
      editorHeader.appendChild(velModeBtn);

      // Step grid
      const grid = document.createElement('div');
      grid.style.cssText = 'display:flex;flex-direction:column;gap:2px;padding:4px;';

      // Step number header
      state.previewStepCells = [];
      const headerRow = document.createElement('div');
      headerRow.style.cssText = 'display:flex;align-items:center;gap:1px;padding-left:141px;';
      for (let s = 0; s < seq.steps; s++) {
         const cell = document.createElement('div');
         cell.dataset.step = String(s);
         cell.style.cssText = `width:20px;height:14px;text-align:center;font-family:bit,monospace;font-size:8px;color:${s % 4 === 0 ? 'var(--cf)' : 'var(--c6)'};`;
         cell.textContent = (s + 1);
         state.previewStepCells.push(cell);
         headerRow.appendChild(cell);
      }
      grid.appendChild(headerRow);

      // Empty state hint
      if (seq.channels.length === 0) {
         const hint = document.createElement('div');
         hint.style.cssText = 'padding:16px;font-family:bit,monospace;font-size:10px;color:var(--c6);text-align:center;';
         hint.textContent = '← Drag & drop samples from the bucket to add channels';
         grid.appendChild(hint);
      }

      // Channel rows
      for (let ci = 0; ci < seq.channels.length; ci++) {
         const ch = seq.channels[ci];
         const row = document.createElement('div');
         row.style.cssText = 'display:flex;align-items:center;gap:1px;';

         // Channel controls: waveform card + volume knob + delete
         const chCtrl = document.createElement('div');
         chCtrl.style.cssText = 'width:140px;display:flex;align-items:center;gap:3px;';

         // Delete channel (first in line)
         const delChBtn = document.createElement('button');
         delChBtn.textContent = 'x';
         delChBtn.style.cssText = 'color:var(--c6);cursor:pointer;font-size:12px;padding:2px 6px;flex-shrink:0;background:var(--c2);border:1px solid var(--c4);border-radius:2px;line-height:1;';
         delChBtn.onmouseover = () => { delChBtn.style.background = 'var(--c5)'; delChBtn.style.color = 'var(--cf)'; };
         delChBtn.onmouseout = () => { delChBtn.style.background = 'var(--c2)'; delChBtn.style.color = 'var(--c6)'; };
         delChBtn.onclick = () => {
            seq.channels.splice(ci, 1);
            renderEditor();
            state.onChange();
         };
         chCtrl.appendChild(delChBtn);

         // Waveform card showing the sample
         const card = document.createElement('div');
         card.style.cssText = 'flex:1;height:28px;background:var(--c0);border:1px solid var(--c4);position:relative;overflow:hidden;cursor:pointer;display:flex;align-items:center;';
         card.title = ch.sampleName || '(empty)';

         // Sample name label
         const nameLabel = document.createElement('div');
         nameLabel.style.cssText = 'position:absolute;top:1px;left:2px;font-family:bit,monospace;font-size:7px;color:var(--ce);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;pointer-events:none;z-index:2;text-shadow:0 0 3px var(--c0);';
         nameLabel.textContent = ch.sampleName || '(empty)';
         card.appendChild(nameLabel);

         // Waveform canvas
         const wfCanvas = document.createElement('canvas');
         wfCanvas.style.cssText = 'width:100%;height:100%;display:block;';
         wfCanvas.width = 120;
         wfCanvas.height = 28;
         card.appendChild(wfCanvas);

         // Draw waveform if sample is loaded
         if (ch.sampleId) {
            const eng = state.getEngine();
            if (eng) {
               const drawWf = () => {
                  const buf = eng.getMIDISampleBuffer(ch.sampleId);
                  if (buf) {
                     drawChannelWaveform(wfCanvas, buf);
                  } else {
                     eng.getMIDISampleBuffer(ch.sampleId);
                     setTimeout(drawWf, 200);
                  }
               };
               drawWf();
            }
         }

         // Allow drag & drop to replace sample
         card.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.stopPropagation();
            card.style.borderColor = 'var(--c9)';
         });
         card.addEventListener('dragleave', () => {
            card.style.borderColor = 'var(--c4)';
         });
         card.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            card.style.borderColor = 'var(--c4)';
            if (state.resolveDropItem) {
               const item = await state.resolveDropItem(e.dataTransfer);
               if (item) {
                  ch.sampleId = item.id;
                  ch.sampleName = item.name;
                  renderEditor();
                  state.onChange();
                  return;
               }
            }
            const itemId = e.dataTransfer.getData('application/x-bucket-item-id') ||
                           e.dataTransfer.getData('text/plain');
            if (itemId && state.getBucketItems) {
               const item = state.getBucketItems().find(it => it.id === itemId);
               if (item) {
                  ch.sampleId = item.id;
                  ch.sampleName = item.name;
                  renderEditor();
                  state.onChange();
               }
            }
         });

         chCtrl.appendChild(card);

         // Volume knob (same as mixer)
         const volKnob = makeKnob('VOL', 0, 1, 0.01, ch.volume, (v) => { ch.volume = v; state.onChange(); }, null, { id: 'stepseq_' + ch.id + '_vol', noLabels: true });
         volKnob.style.cssText = 'transform:scale(0.85);transform-origin:center;';
         chCtrl.appendChild(volKnob);

         row.appendChild(chCtrl);

         // Step cells
         for (let s = 0; s < seq.steps; s++) {
            const cell = document.createElement('div');
            const isBeat = s % 4 === 0;
            const isOn = ch.steps[s] !== false && ch.steps[s] !== undefined && ch.steps[s] !== null;
            const pitch = typeof ch.steps[s] === 'number' ? ch.steps[s] : 0; // semitone offset
            const hasPitch = isOn && pitch !== 0;
            cell.style.cssText = `width:20px;height:20px;border:1px solid var(--c2);cursor:pointer;position:relative;background:${isOn ? (isBeat ? 'var(--cb)' : 'var(--c9)') : (isBeat ? 'var(--c1)' : 'var(--c0)')};overflow:hidden;`;
            if (hasPitch) {
               cell.title = 'Pitch: ' + (pitch > 0 ? '+' : '') + pitch + ' semitones (right-click to change)';
               const pitchLabel = document.createElement('span');
               pitchLabel.style.cssText = 'position:absolute;top:0;left:1px;font-size:7px;color:var(--c0);font-family:bit,monospace;pointer-events:none;line-height:1;';
               pitchLabel.textContent = (pitch > 0 ? '+' : '') + pitch;
               cell.appendChild(pitchLabel);
            }
            cell.onclick = () => {
               const wasOn = ch.steps[s] !== false && ch.steps[s] !== undefined && ch.steps[s] !== null;
               ch.steps[s] = wasOn ? false : true;
               if (!ch.velocities) ch.velocities = [];
               if (!wasOn && ch.velocities[s] === undefined) ch.velocities[s] = 1.0;
               renderEditor();
               state.onChange();
            };
            cell.oncontextmenu = (e) => {
               e.preventDefault();
               e.stopPropagation();
               const wasOn = ch.steps[s] !== false && ch.steps[s] !== undefined && ch.steps[s] !== null;
               if (!wasOn) {
                  // Turn on with default pitch first
                  ch.steps[s] = true;
                  renderEditor();
                  state.onChange();
               }
               showPitchKeyboard(e, ch, s, () => { renderEditor(); state.onChange(); });
            };
            row.appendChild(cell);
         }

         grid.appendChild(row);

         // Velocity knob row (only when VEL mode is active)
         if (state.velMode) {
            const velRow = document.createElement('div');
            velRow.style.cssText = 'display:flex;align-items:center;gap:1px;padding-left:141px;';
            for (let s = 0; s < seq.steps; s++) {
               const isOn = ch.steps[s] !== false && ch.steps[s] !== undefined && ch.steps[s] !== null;
               if (!ch.velocities) ch.velocities = [];
               const vel = ch.velocities[s] !== undefined ? ch.velocities[s] : 1.0;
               const velWrap = document.createElement('div');
               velWrap.style.cssText = 'width:20px;display:flex;justify-content:center;';
               if (isOn) {
                  const velKnob = makeKnob('', 0, 1, 0.01, vel, (v) => {
                     ch.velocities[s] = v;
                     state.onChange();
                  }, null, { noLabels: true, id: 'stepseq_' + ch.id + '_vel_' + s });
                  velKnob.style.cssText = 'transform:scale(0.5);transform-origin:center;';
                  velWrap.appendChild(velKnob);
               }
               velRow.appendChild(velWrap);
            }
            grid.appendChild(velRow);
         }
      }

      editorBody.appendChild(grid);
   }

   // ---- Get the active sequence for drag & drop ----
   function getActiveSequence() {
      return state.sequences[state.activeSeqIdx] || null;
   }

   // ---- Get all sequences (for saving) ----
   function getSequences() {
      return state.sequences.map(s => JSON.parse(JSON.stringify(s)));
   }

   // Get live sequence object by index (reference, not copy)
   // Used for real-time updates when editing a sequence that's on the timeline
   function getSequenceRef(idx) {
      return state.sequences[idx] || null;
   }

   // ---- Load sequences (for loading) ----
   function loadSequences(seqs) {
      state.sequences = seqs || [];
      // Ensure all sequences have UIDs (for live-reference linking on load)
      // and all channels have unique IDs (for mono voice isolation)
      for (const seq of state.sequences) {
         if (!seq.uid) {
            seq.uid = 'seq_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
         }
         if (!seq.channels) continue;
         for (const ch of seq.channels) {
            if (!ch.id) {
               ch.id = 'ch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
            }
         }
      }
      state.activeSeqIdx = 0;
      renderList();
      renderEditor();
   }

   // ---- Initialize with one empty sequence (no channels) ----
   if (state.sequences.length === 0) {
      createNewSequence();
   } else {
      renderList();
      renderEditor();
   }

   return {
      getActiveSequence,
      getSequences,
      getSequenceRef,
      loadSequences,
      addChannelFromSample,
      stopPreview,
      render: () => { renderList(); renderEditor(); },
   };
}

module.exports = { createStepSequencer };
